import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../context/ApiProvider';
import type {
  CreateOnboardingTaskRequest,
  MyOnboardingTasksResponse,
  OnboardingOverviewResponse,
  OnboardingTaskStatus,
  OnboardingUserTasksResponse,
  ReorderOnboardingTasksRequest,
} from '../api/onboarding';

export function useMyOnboardingTasks(enabled = true) {
  const api = useApi();
  return useQuery<MyOnboardingTasksResponse>({
    queryKey: ['onboarding', 'my'],
    queryFn: () => api.get('/onboarding/tasks/me').then((r) => r.data),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useOnboardingOverview(
  role?: 'client' | 'agent' | 'admin',
  enabled = true,
) {
  const api = useApi();
  return useQuery<OnboardingOverviewResponse>({
    queryKey: ['onboarding', 'overview', role ?? 'all'],
    queryFn: () =>
      api
        .get('/onboarding/tasks/overview', {
          params: role ? { role } : undefined,
        })
        .then((r) => r.data),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useOnboardingUserTasks(userId: string | null, enabled = true) {
  const api = useApi();
  return useQuery<OnboardingUserTasksResponse>({
    queryKey: ['onboarding', 'user', userId],
    queryFn: () => api.get(`/onboarding/tasks/user/${userId}`).then((r) => r.data),
    enabled: enabled && Boolean(userId),
    staleTime: 10 * 1000,
  });
}

export function useCreateOnboardingTask() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOnboardingTaskRequest) =>
      api.post('/onboarding/tasks', payload).then((r) => r.data),
    onSuccess: (_data, payload) => {
      qc.invalidateQueries({ queryKey: ['onboarding'] });
      qc.invalidateQueries({ queryKey: ['onboarding', 'user', payload.userId] });
    },
  });
}

export function useReorderOnboardingTasks() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReorderOnboardingTasksRequest) =>
      api.post('/onboarding/tasks/reorder', payload).then((r) => r.data),
    onSuccess: (_data, payload) => {
      qc.invalidateQueries({ queryKey: ['onboarding'] });
      qc.invalidateQueries({ queryKey: ['onboarding', 'user', payload.userId] });
    },
  });
}

export function useUpdateOnboardingTaskStatus() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      status,
    }: {
      taskId: string;
      status: OnboardingTaskStatus;
    }) =>
      api
        .patch(`/onboarding/tasks/${taskId}/status`, { status })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

export function useDeleteOnboardingTask() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      api.delete(`/onboarding/tasks/${taskId}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

